import { defineMessages, plural } from "../i18n/core";

export const policiesMessages = defineMessages({
  "pt-BR": {
    title: "Políticas",
    lede: "Uma política define o que copiar, quando e por quanto tempo guardar. Cada máquina segue exatamente uma.",
    newPolicy: "Nova política",
    colPolicy: "Política",
    colSchedule: "Agenda",
    colRpo: "RPO",
    colRetention: "Retenção",
    colMachines: "Máquinas",
    reach: (reach: string) => `alcança ${reach}`,

    eyebrow: "Política",
    fallbackTitle: "Política",
    followers: (n: number) => `${n} ${plural(n, "máquina segue", "máquinas seguem")} esta política.`,
    newLede: "Defina o que proteger e por quanto tempo.",
    savePolicy: "Salvar política",
    nameRequired: "Dê um nome à política.",
    retentionRequired: "Mantenha ao menos um snapshot.",
    created: (name: string) => `Política "${name}" criada.`,
    saved: (name: string) => `Política "${name}" salva. As máquinas recebem a mudança na próxima sessão.`,
    saveFailed: "Não foi possível salvar.",
    selection: "Seleção",
    name: "Nome",
    include: "Incluir",
    includeHint: "Um caminho por linha. Caminhos inexistentes na máquina são ignorados, então uma política serve Windows e macOS.",
    exclude: "Excluir",
    excludeHintPrefix: "Padrões com * e **. Ex.:",
    schedule: "Agenda",
    frequency: "Frequência",
    frequencyOption: { hourly: "A cada hora", daily: "Diária", weekly: "Semanal" },
    startMinute: "Minuto de início",
    time: "Horário",
    windowHours: "Janela (horas)",
    rpoHours: "RPO (horas)",
    rpoHint: "Após esse tempo sem backup, a máquina fica Atrasada.",
    days: "Dias",
    retention: "Retenção",
    retentionSummary: (reach: string, text: string) => `Alcança ${reach} · ${text}`,
    retentionField: { daily: "Diários", weekly: "Semanais", monthly: "Mensais", yearly: "Anuais" },
    retentionHint: "Um snapshot é mantido se qualquer regra o reter. Limpeza feita pelo servidor; o agente nunca apaga backups.",
    destination: "Destino e desempenho",
    poolAutomatic: "O destino é escolhido pela plataforma entre as áreas contratadas, sempre a com mais espaço livre. Não há nada para escolher aqui.",
    osSnapshot: "Snapshot do sistema antes de copiar",
    osSnapshotHint: "VSS no Windows, APFS no macOS, LVM ou btrfs no Linux. Garante consistência de arquivos abertos.",
    bandwidth: "Limite de banda (Mbps)",
    noLimit: "Sem limite",
    image: "Imagem de disco",
    imageEnable: "Copiar a imagem dos discos",
    imageEnableHint: "Copia o disco inteiro: tabela de partição, setor de boot e todos os volumes. É o que permite recuperar a máquina do zero.",
    imageStorage: "A imagem ocupa e trafega bem mais que arquivos, e conta no volume contratado. O espaço livre do disco não é copiado em NTFS, ext2, ext3 e ext4.",
    imageConsistency: "No Windows o instantâneo é feito pelo VSS. No Linux, com LVM o agente tira instantâneo; sem LVM a imagem fica consistente por queda, como um desligamento abrupto — bancos de dados precisam do backup próprio deles.",
    imageMac: "Máquinas macOS continuam apenas com backup de arquivos: a Apple não tem caminho suportado para restaurar imagem de APFS em outra máquina.",
    imageDisks: "Discos",
    imageDisksHint: "Um por linha, como o agente informa: /dev/sda no Linux, \\\\.\\PHYSICALDRIVE0 no Windows. Vazio copia os discos de sistema de cada máquina.",
  },
  "en-US": {
    title: "Policies",
    lede: "A policy defines what to copy, when and how long to keep it. Each machine follows exactly one.",
    newPolicy: "New policy",
    colPolicy: "Policy",
    colSchedule: "Schedule",
    colRpo: "RPO",
    colRetention: "Retention",
    colMachines: "Machines",
    reach: (reach: string) => `reaches ${reach}`,

    eyebrow: "Policy",
    fallbackTitle: "Policy",
    followers: (n: number) => `${n} ${plural(n, "machine follows", "machines follow")} this policy.`,
    newLede: "Define what to protect and for how long.",
    savePolicy: "Save policy",
    nameRequired: "Give the policy a name.",
    retentionRequired: "Keep at least one snapshot.",
    created: (name: string) => `Policy "${name}" created.`,
    saved: (name: string) => `Policy "${name}" saved. Machines receive the change in their next session.`,
    saveFailed: "Could not save.",
    selection: "Selection",
    name: "Name",
    include: "Include",
    includeHint: "One path per line. Paths that do not exist on the machine are skipped, so one policy works for Windows and macOS.",
    exclude: "Exclude",
    excludeHintPrefix: "Patterns with * and **. E.g.:",
    schedule: "Schedule",
    frequency: "Frequency",
    frequencyOption: { hourly: "Hourly", daily: "Daily", weekly: "Weekly" },
    startMinute: "Start minute",
    time: "Time",
    windowHours: "Window (hours)",
    rpoHours: "RPO (hours)",
    rpoHint: "After this long without a backup, the machine becomes Overdue.",
    days: "Days",
    retention: "Retention",
    retentionSummary: (reach: string, text: string) => `Reaches ${reach} · ${text}`,
    retentionField: { daily: "Daily", weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" },
    retentionHint: "A snapshot is kept if any rule retains it. Cleanup is done by the server; the agent never deletes backups.",
    destination: "Destination and performance",
    poolAutomatic: "The platform picks the destination among the contracted areas, always the one with the most free space. There is nothing to choose here.",
    osSnapshot: "System snapshot before copying",
    osSnapshotHint: "VSS on Windows, APFS on macOS, LVM or btrfs on Linux. Keeps open files consistent.",
    bandwidth: "Bandwidth limit (Mbps)",
    noLimit: "No limit",
    image: "Disk image",
    imageEnable: "Copy the disk image",
    imageEnableHint: "Copies the whole disk: partition table, boot sector and every volume. This is what lets you rebuild the machine from scratch.",
    imageStorage: "An image stores and transfers far more than files, and counts against the contracted volume. Free disk space is skipped on NTFS, ext2, ext3 and ext4.",
    imageConsistency: "On Windows the snapshot is taken by VSS. On Linux, with LVM the agent takes a snapshot; without LVM the image is crash-consistent, like an abrupt shutdown — databases need their own backup.",
    imageMac: "macOS machines stay on file backup only: Apple offers no supported way to restore an APFS image onto another machine.",
    imageDisks: "Disks",
    imageDisksHint: "One per line, as the agent reports them: /dev/sda on Linux, \\\\.\\PHYSICALDRIVE0 on Windows. Empty copies each machine's system disks.",
  },
  es: {
    title: "Políticas",
    lede: "Una política define qué copiar, cuándo y cuánto tiempo conservarlo. Cada equipo sigue exactamente una.",
    newPolicy: "Nueva política",
    colPolicy: "Política",
    colSchedule: "Programación",
    colRpo: "RPO",
    colRetention: "Retención",
    colMachines: "Equipos",
    reach: (reach: string) => `alcanza ${reach}`,

    eyebrow: "Política",
    fallbackTitle: "Política",
    followers: (n: number) => `${n} ${plural(n, "equipo sigue", "equipos siguen")} esta política.`,
    newLede: "Define qué proteger y por cuánto tiempo.",
    savePolicy: "Guardar política",
    nameRequired: "Ponle un nombre a la política.",
    retentionRequired: "Conserva al menos un snapshot.",
    created: (name: string) => `Política "${name}" creada.`,
    saved: (name: string) => `Política "${name}" guardada. Los equipos reciben el cambio en su próxima sesión.`,
    saveFailed: "No se pudo guardar.",
    selection: "Selección",
    name: "Nombre",
    include: "Incluir",
    includeHint: "Una ruta por línea. Las rutas que no existen en el equipo se omiten, así una política sirve para Windows y macOS.",
    exclude: "Excluir",
    excludeHintPrefix: "Patrones con * y **. Ej.:",
    schedule: "Programación",
    frequency: "Frecuencia",
    frequencyOption: { hourly: "Cada hora", daily: "Diaria", weekly: "Semanal" },
    startMinute: "Minuto de inicio",
    time: "Hora",
    windowHours: "Ventana (horas)",
    rpoHours: "RPO (horas)",
    rpoHint: "Pasado este tiempo sin copia de seguridad, el equipo queda Atrasado.",
    days: "Días",
    retention: "Retención",
    retentionSummary: (reach: string, text: string) => `Alcanza ${reach} · ${text}`,
    retentionField: { daily: "Diarias", weekly: "Semanales", monthly: "Mensuales", yearly: "Anuales" },
    retentionHint: "Un snapshot se conserva si cualquier regla lo retiene. La limpieza la hace el servidor; el agente nunca borra copias de seguridad.",
    destination: "Destino y rendimiento",
    poolAutomatic: "La plataforma elige el destino entre las áreas contratadas, siempre la que tiene más espacio libre. Aquí no hay nada que elegir.",
    osSnapshot: "Snapshot del sistema antes de copiar",
    osSnapshotHint: "VSS en Windows, APFS en macOS, LVM o btrfs en Linux. Garantiza la consistencia de los archivos abiertos.",
    bandwidth: "Límite de ancho de banda (Mbps)",
    noLimit: "Sin límite",
    image: "Imagen de disco",
    imageEnable: "Copiar la imagen de los discos",
    imageEnableHint: "Copia el disco entero: tabla de particiones, sector de arranque y todos los volúmenes. Es lo que permite rehacer el equipo desde cero.",
    imageStorage: "La imagen ocupa y transfiere mucho más que los archivos, y cuenta en el volumen contratado. El espacio libre no se copia en NTFS, ext2, ext3 y ext4.",
    imageConsistency: "En Windows la instantánea la hace VSS. En Linux, con LVM el agente toma una instantánea; sin LVM la imagen queda consistente por caída, como un apagado abrupto: las bases de datos necesitan su propia copia.",
    imageMac: "Los equipos macOS siguen solo con copia de archivos: Apple no ofrece una forma admitida de restaurar una imagen APFS en otro equipo.",
    imageDisks: "Discos",
    imageDisksHint: "Uno por línea, como los informa el agente: /dev/sda en Linux, \\\\.\\PHYSICALDRIVE0 en Windows. Vacío copia los discos de sistema de cada equipo.",
  },
});
